<script lang="ts">
  import type { CTAOptions } from "@kontextwerk/web-sdk/ui/actions/cta/types"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import {
    buildRichTooltip,
    RICH_TOOLTIP_CLASS,
    tooltipField,
  } from "../../../../../_shared/_logic/rich-tooltips"

  type CTAType =
    | "confirmationButton"
    | "toggle"
    | "primaryButton"
    | "secondaryButton"
    | "tertiaryButton"
    | "tableButton"
    | "ghostButton"
    | "ghostButtonReverse"
    | "contentLessButton"
    | "tableButtonWithContent"
    | "BubbleButton"
    | "normal"

  type Props = {
    options?: CTAOptions
    type: CTAType
    icon?: string
    disabled?: boolean
    disabledReason?: string | null
    content?: string
    darkMode?: boolean
    small?: boolean
    style?: string
    tooltipContent?: string | null
    tooltipClassName?: string
    dataTestid?: string
    onClick?: (event: MouseEvent) => void
  }

  let {
    type,
    options = undefined,
    icon = "",
    disabled = false,
    disabledReason = null,
    content = "",
    darkMode = true,
    small = false,
    style = "",
    tooltipContent = null,
    tooltipClassName = "",
    dataTestid = undefined,
    onClick = () => {},
  }: Props = $props()

  const disabledTooltipContent = $derived(
    disabledReason ? buildRichTooltip([tooltipField("Hinweis", disabledReason)]) : tooltipContent,
  )
  const shouldAnchorTooltip = $derived(
    Boolean(disabled && disabledTooltipContent && !options?.items.length),
  )
  const wrapperTooltipProps = $derived(
    shouldAnchorTooltip
      ? {
          content: disabledTooltipContent ?? "",
          className: tooltipClassName || RICH_TOOLTIP_CLASS,
        }
      : false,
  )
  const buttonTooltipContent = $derived(
    shouldAnchorTooltip ? null : disabled ? disabledTooltipContent : tooltipContent,
  )
  const buttonTooltipClassName = $derived(
    shouldAnchorTooltip ? "" : disabled ? tooltipClassName || RICH_TOOLTIP_CLASS : tooltipClassName,
  )
  const effectiveOnClick = $derived(disabled ? () => {} : onClick)

  const handleWrapperKeydown = (event: KeyboardEvent) => {
    if (!shouldAnchorTooltip) return

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
    }
  }

  const handleWrapperClick = (event: MouseEvent) => {
    if (!shouldAnchorTooltip) return

    event.preventDefault()
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex (tabindex is only set together with role=button for disabled tooltip anchoring) -->
<span
  class:disabled-anchor={shouldAnchorTooltip}
  class="step-action-cta"
  role={shouldAnchorTooltip ? "button" : undefined}
  tabindex={shouldAnchorTooltip ? 0 : undefined}
  aria-disabled={shouldAnchorTooltip ? "true" : undefined}
  aria-label={shouldAnchorTooltip
    ? disabledReason
      ? `Warum deaktiviert: ${disabledReason}`
      : "Warum deaktiviert"
    : undefined}
  title={shouldAnchorTooltip ? disabledReason || undefined : undefined}
  onclick={handleWrapperClick}
  onkeydown={handleWrapperKeydown}
  use:tooltip={wrapperTooltipProps}
>
  <CTA
    {type}
    {options}
    {icon}
    {disabled}
    {content}
    {darkMode}
    {small}
    {style}
    tooltipContent={buttonTooltipContent}
    tooltipClassName={buttonTooltipClassName}
    {dataTestid}
    onClick={effectiveOnClick}
  />
</span>

<style>
  .step-action-cta {
    display: inline-flex;
    width: fit-content;
  }

  .step-action-cta.disabled-anchor {
    cursor: not-allowed;
  }
</style>
